/**
 * DEV-101 §24.3: отказ от бэкенда надо узнать раньше, чем читать из него данные.
 *
 * Бэкенд отвечает отказом в двух разных формах, и это не лень, а история:
 *
 * 1. Конверт с HTTP 200: `{ data: null, error: true, message, code? }` - так отвечает
 *    `checkClientSession`, чтобы не ломать старых потребителей.
 * 2. HTTP 401/403/503 с телом `{ status: 'UNAUTHORIZED' | 'FORBIDDEN' | 'AUTH_UNAVAILABLE' }`
 *    - так отвечают `checkPrivate` и гварды кросс-продуктов.
 *
 * Фронт до сих пор смотрел только на `error` из первой формы. Вторая проезжала мимо: тело
 * отказа шло дальше как данные. В опросе статуса ПКаско это давало `status: 'FORBIDDEN'` -
 * то есть отказ доступа притворялся статусом полиса.
 *
 * Здесь обе формы сводятся к одному ответу на три вопроса: сессия кончилась, прав не хватает
 * или сервис авторизации недоступен. Тексты русские: их видит клиент.
 */
export type AuthFailureKind = 'session' | 'forbidden' | 'unavailable';

export interface AuthFailure {
    kind: AuthFailureKind;
    message: string;
}

const MESSAGES: Record<AuthFailureKind, string> = {
    session: 'Сессия истекла, войдите заново',
    forbidden: 'Нет доступа к этому разделу',
    unavailable: 'Проверка доступа временно недоступна, попробуйте позже',
};

/** Отказ ли это, и какой именно. `null` - обычный ответ, читаем как данные. */
export function classifyAuthFailure(
    httpStatus: number,
    body: unknown,
): AuthFailure | null {
    const payload = (body || {}) as Record<string, unknown>;
    const status = typeof payload.status === 'string' ? payload.status : '';
    const code = typeof payload.code === 'string' ? payload.code : '';

    if (httpStatus === 503 || status === 'AUTH_UNAVAILABLE' || code === 'AUTH_UNAVAILABLE') {
        return { kind: 'unavailable', message: MESSAGES.unavailable };
    }
    if (httpStatus === 403 || status === 'FORBIDDEN') {
        return { kind: 'forbidden', message: MESSAGES.forbidden };
    }
    if (httpStatus === 401 || status === 'UNAUTHORIZED') {
        return { kind: 'session', message: MESSAGES.session };
    }

    // Конверт checkClientSession: HTTP 200, но внутри отказ. Сообщение английское и
    // клиенту ничего не объясняет, поэтому подменяем своим.
    if (payload.error === true && payload.message === 'Unauthorized') {
        return { kind: 'session', message: MESSAGES.session };
    }

    return null;
}

/**
 * Что делать при потере сессии. Реализацию ставит приложение (`app.tsx`), чтобы слой
 * запросов не знал ни про роутер, ни про стор.
 */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(handler: (() => void) | null): void {
    onSessionLost = handler;
}

export function handleAuthFailure(failure: AuthFailure): void {
    if (failure.kind === 'session' && onSessionLost) {
        onSessionLost();
    }
}
